import { CodeStatus, PermitType, SignatureType } from "../../common/enums";
import { formElement } from "../forms/FormElement";
import HullCleaningPermitFormState from "../forms/Permits/HullCleaningPermitFormState";
import Person, { Person_default, Person_toFormElement } from "../Person";
import Position, { Position_toPositionPickerRow } from "../Position";
import Signature, { Signature_empty } from "../Signature";
import { permitContentVersions } from "../WorkPermit";

type HullCleaningPermit = {
    id: string;
    entityId: number;
    sequenceIdentifier: string;
    remindMe: boolean;
    remindMeDate: Date;
    startDate: Date;
    endDate: Date;
    adminComment: string;
    status: CodeStatus;
    description: string;
    positions: Position[];
    anonymousUserEmail: string;

    permitProviderSignature: Signature;
    expirationReminderEmail: string;
    expirationReminderPhoneNumber: string;
    version: number;
    hasAttachedFiles: boolean;
    entrepreneur: string;
    renewalOfPermitSequenceIdentifier: string;
    renewedByPermitSequenceIdentifier: string;
    createdByUserName: string;
    rejectionComment: string;
    permitName: PermitType.HullCleaningPermit;

    shipName: string;
    callSign: string;
    shipRoute: string;
    colorSystem: string;
    cleaningMethod: string;
    methodIsApproved: boolean;
    contactPerson: Person;
    isAnonymousPermit: boolean;
    initialStartDate: Date;
    extendedEndDate: Date;

    departmentId: number;

    contentVersion: number;

    canUpdate: boolean;
    canAccept: boolean;
    canManageAttachments: boolean;
    canManagePositions: boolean;
    canReject: boolean;
    canManageAdminComment: boolean;
    canRenew: boolean;
    canManagePositionPolygons: boolean;
};

export default HullCleaningPermit;

export function HullCleaningPermit_normalize(hullCleaningPermit: HullCleaningPermit): HullCleaningPermit {
    const normalizedHullCleaningPermit: HullCleaningPermit = {
        adminComment: hullCleaningPermit.adminComment ?? "",
        callSign: hullCleaningPermit.callSign ?? "",
        cleaningMethod: hullCleaningPermit.cleaningMethod ?? "",
        colorSystem: hullCleaningPermit.colorSystem ?? "",
        contactPerson: hullCleaningPermit.contactPerson ?? Person_default(),
        createdByUserName: hullCleaningPermit.createdByUserName ?? "",
        description: hullCleaningPermit.description ?? "",
        canAccept: hullCleaningPermit.canAccept ?? false,
        canManageAdminComment: hullCleaningPermit.canManageAdminComment ?? false,
        canManageAttachments: hullCleaningPermit.canManageAttachments ?? false,
        canManagePositions: hullCleaningPermit.canManagePositions ?? false,
        canReject: hullCleaningPermit.canReject ?? false,
        canRenew: hullCleaningPermit.canRenew ?? false,
        canUpdate: hullCleaningPermit.canUpdate ?? false,
        departmentId: hullCleaningPermit.departmentId ?? 0,
        endDate: hullCleaningPermit.endDate ? new Date(hullCleaningPermit.endDate) : new Date(),
        entityId: hullCleaningPermit.entityId ?? 0,
        entrepreneur: hullCleaningPermit.entrepreneur ?? "",
        expirationReminderEmail: hullCleaningPermit.expirationReminderEmail ?? "",
        expirationReminderPhoneNumber: hullCleaningPermit.expirationReminderPhoneNumber ?? "",
        hasAttachedFiles: hullCleaningPermit.hasAttachedFiles ?? false,
        id: hullCleaningPermit.id ?? "",
        methodIsApproved: hullCleaningPermit.methodIsApproved ?? false,
        positions: hullCleaningPermit.positions ?? [],
        remindMe: hullCleaningPermit.remindMe ?? false,
        remindMeDate: hullCleaningPermit.remindMeDate ? new Date(hullCleaningPermit.remindMeDate) : new Date(),
        shipName: hullCleaningPermit.shipName ?? "",
        shipRoute: hullCleaningPermit.shipRoute ?? "",
        status: hullCleaningPermit.status ?? CodeStatus.Registered,
        version: hullCleaningPermit.version ?? 1,
        permitProviderSignature:
            hullCleaningPermit.permitProviderSignature ?? Signature_empty(SignatureType.PermitProvider),
        rejectionComment: hullCleaningPermit.rejectionComment ?? "",
        renewalOfPermitSequenceIdentifier: hullCleaningPermit.renewalOfPermitSequenceIdentifier ?? "",
        renewedByPermitSequenceIdentifier: hullCleaningPermit.renewedByPermitSequenceIdentifier ?? "",
        startDate: hullCleaningPermit.startDate ? new Date(hullCleaningPermit.startDate) : new Date(),
        sequenceIdentifier: hullCleaningPermit.sequenceIdentifier ?? "",
        permitName: PermitType.HullCleaningPermit,
        anonymousUserEmail: hullCleaningPermit.anonymousUserEmail ?? "",
        isAnonymousPermit: hullCleaningPermit.isAnonymousPermit ?? false,
        extendedEndDate: hullCleaningPermit.extendedEndDate ? new Date(hullCleaningPermit.extendedEndDate) : new Date(),
        initialStartDate: hullCleaningPermit.initialStartDate
            ? new Date(hullCleaningPermit.initialStartDate)
            : new Date(),
        canManagePositionPolygons: hullCleaningPermit.canManagePositionPolygons ?? false,
        contentVersion: hullCleaningPermit.contentVersion ?? Math.max(...Object.values(permitContentVersions))
    };

    return normalizedHullCleaningPermit;
}

export function HullCleaningPermit_toFormState(hullCleaningPermit: HullCleaningPermit): HullCleaningPermitFormState {
    const formState: HullCleaningPermitFormState = {
        adminComment: formElement(hullCleaningPermit.adminComment),
        callSign: formElement(hullCleaningPermit.callSign),
        cleaningMethod: formElement(hullCleaningPermit.cleaningMethod),
        colorSystem: formElement(hullCleaningPermit.colorSystem),
        contactPerson: Person_toFormElement(hullCleaningPermit.contactPerson),
        description: formElement(hullCleaningPermit.description),
        endDate: formElement(hullCleaningPermit.endDate),
        entrepreneur: formElement(hullCleaningPermit.entrepreneur),
        expirationReminderEmail: formElement(hullCleaningPermit.expirationReminderEmail),
        expirationReminderPhoneNumber: formElement(hullCleaningPermit.expirationReminderPhoneNumber),
        methodIsApproved: formElement(hullCleaningPermit.methodIsApproved),
        positions: hullCleaningPermit.positions.map(Position_toPositionPickerRow),
        remindMe: formElement(hullCleaningPermit.remindMe),
        remindMeDate: formElement(hullCleaningPermit.remindMeDate),
        shipName: formElement(hullCleaningPermit.shipName),
        shipRoute: formElement(hullCleaningPermit.shipRoute),
        startDate: formElement(hullCleaningPermit.startDate),
        anonymousUserEmail: formElement(hullCleaningPermit.anonymousUserEmail)
    };

    return formState;
}
